import { Box, CircularProgress } from '@mui/material';
import { useAnalytics } from 'contexts/Analytics/Analytics';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as setup from 'setup';

const TOKEN_STORAGE_KEY = 'kromatic-user-t';
const AUID_STORAGE_KEY = 'kromatic-user-auid';
const GAME_SLUG = 'startup-poker';

/**
 * PII HARD RULE (#481 / RSB#735): the GA4 user_id MUST be the salted-HMAC `auid`
 * pseudonym from orca-back, NEVER the raw account id, email, or name. Reject
 * blank values and all-digit values (a raw numeric DB id would be all-digits)
 * so nothing PII-bearing is ever cached or emitted.
 */
function getValidAuid(value: unknown): string | null {
    if (typeof value !== 'string') {
        return null;
    }
    const trimmed = value.trim();
    if (!trimmed || /^\d+$/.test(trimmed)) {
        return null;
    }
    return trimmed;
}

const ORCA_PATH_MAP: Record<string, string> = {
    'auth/user': 'user/info/',
};

function resolveOrcaPath(path: string): string {
    if (ORCA_PATH_MAP[path]) return ORCA_PATH_MAP[path];
    if (path.startsWith('kromatic/game-progress/')) {
        return path.replace('kromatic/game-progress/', 'kromatic/user/game-progress/');
    }
    return path;
}

function resolveAuthApiBaseUrl(): string {
    if (typeof window === 'undefined') return '';
    const hostname = window.location.hostname;
    if (hostname === 'staging.kromatic.com') return 'https://staging-api.kromatic.com';
    if (hostname === 'kromatic.com' || hostname === 'www.kromatic.com') return 'https://api.kromatic.com';
    return process.env.NEXT_PUBLIC_AUTH_API_URL || '';
}

function resolveWorkerApiBaseUrl(): string {
    if (typeof window === 'undefined') return '';
    const hostname = window.location.hostname;
    if (hostname === 'staging.kromatic.com') return 'https://startup-poker-api.kromatic.workers.dev';
    if (hostname === 'kromatic.com' || hostname === 'www.kromatic.com') return 'https://startup-poker-api.kromatic.workers.dev';
    return process.env.NEXT_PUBLIC_WORKER_API_URL || 'http://localhost:8787';
}

type ApiRequestOptions = {
    body?: Record<string, any>;
    method?: string;
    token?: string | null;
};

const authEnabled = !!process.env.AUTH_API || (typeof window !== 'undefined' && !!resolveAuthApiBaseUrl());

const ApiContext = createContext<ApiState>({} as ApiState);

export const useApi = () => useContext(ApiContext);

export function withApi(WrappedComponent: any) {
    return function ApiWrappedComponent(props: any) {
        return <ApiContext.Consumer>{state => <WrappedComponent {...props} api={state} />}</ApiContext.Consumer>;
    };
}

function getAccessToken(value: any) {
    const token = value?.access_token || value?.token;
    if (typeof token !== 'string') {
        return null;
    }
    const parsedToken = token.replace(/^bearer\s+/i, '').trim();
    return parsedToken || null;
}

function normalizeLesson(value: unknown) {
    if (typeof value !== 'string') {
        return null;
    }

    return setup.lessonKeys.includes(value) ? value : null;
}

function parseProgressLessons(data: any) {
    const highestLesson = normalizeLesson(data?.highest_checkpoint_value ?? data?.highestCheckpointValue);
    const lastLesson = normalizeLesson(data?.last_checkpoint_value ?? data?.lastCheckpointValue);
    return {
        highestLesson,
        lastLesson,
    };
}

function getErrorMessage(error: any, fallbackMessage: string) {
    if (typeof error === 'string') {
        return error;
    }

    if (typeof error?.error === 'string') {
        return error.error;
    }

    if (typeof error?.detail === 'string') {
        return error.detail;
    }

    if (Array.isArray(error?.detail) && error.detail[0]?.msg) {
        return error.detail[0].msg;
    }

    return fallbackMessage;
}

export function ApiProvider({ children }: ApiProps) {
    const [bootstrapped, setBootstrapped] = useState(false);
    const [highestLesson, setHighestLesson] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [ready, setReady] = useState(false);
    const [resumeLesson, setResumeLesson] = useState<string | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const authBaseUrl = useMemo(() => resolveAuthApiBaseUrl(), []);
    const authConfigured = !!authBaseUrl;
    const { trackLeadEvent, identify, clearIdentity } = useAnalytics();

    // Drop the persisted auid + GA4 identity so a stale identity can't outlive
    // the session via the pre-bootstrap re-push (RSB#735 / #481).
    const clearAuid = useCallback(() => {
        if (typeof window !== 'undefined') {
            window.localStorage.removeItem(AUID_STORAGE_KEY);
        }
        clearIdentity();
    }, [clearIdentity]);

    const setStoredToken = useCallback((nextToken: string | null) => {
        if (typeof window !== 'undefined') {
            if (nextToken) {
                window.localStorage.setItem(TOKEN_STORAGE_KEY, nextToken);
            } else {
                window.localStorage.removeItem(TOKEN_STORAGE_KEY);
            }
        }

        setToken(nextToken);
    }, []);

    const authRequest = useCallback(
        async (path: string, options: ApiRequestOptions = {}) => {
            if (!authBaseUrl) {
                throw new Error('Auth API is not configured');
            }

            const resolvedPath = resolveOrcaPath(path.replace(/^\//, ''));
            const activeToken = options.token === undefined ? token : options.token;
            const headers: Record<string, string> = {
                'content-type': 'application/json',
            };
            if (activeToken) {
                headers.authorization = `Bearer ${activeToken}`;
            }

            const url = `${authBaseUrl}/${resolvedPath}`;
            const response = await fetch(url, {
                body: options.body ? JSON.stringify(options.body) : undefined,
                headers,
                method: options.method || 'GET',
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw data;
            }

            return data;
        },
        [authBaseUrl, token]
    );

    const workerPost = useCallback(
        async <TReq, TRes>(path: string, data: TReq): Promise<TRes> => {
            const baseUrl = resolveWorkerApiBaseUrl();
            const response = await fetch(`${baseUrl}/${path}`, {
                body: JSON.stringify(data),
                headers: { 'content-type': 'application/json' },
                method: 'POST',
            });
            return response.json();
        },
        []
    );

    const loadProgress = useCallback(
        async (activeToken: string | null) => {
            if (!authConfigured || !activeToken) {
                setHighestLesson(null);
                setResumeLesson(null);
                return;
            }

            try {
                const data = await authRequest(`kromatic/game-progress/${GAME_SLUG}`, {
                    token: activeToken,
                });
                const progressLessons = parseProgressLessons(data);
                setHighestLesson(progressLessons.highestLesson);
                setResumeLesson(progressLessons.lastLesson);
            } catch (_error) {
                setHighestLesson(null);
                setResumeLesson(null);
            }
        },
        [authConfigured, authRequest]
    );

    const refreshSession = useCallback(
        async (activeToken: string) => {
            if (!authConfigured) {
                setReady(true);
                return;
            }

            try {
                const data = await authRequest('auth/user', {
                    token: activeToken,
                });
                setUser({
                    ...data,
                    email: data.email,
                    firstName: data.first_name ?? data.firstName,
                    lastName: data.last_name ?? data.lastName,
                });
                // Push the salted-HMAC auid pseudonym as the GA4 user_id (#481) and
                // persist it so the Analytics provider can re-push set_user_id BEFORE
                // GTM bootstrap on the next authed load (RSB#735). Never the raw
                // id/email/name; a missing/invalid auid clears any stale identity.
                const auid = getValidAuid(data?.auid);
                if (auid) {
                    identify(auid);
                    if (typeof window !== 'undefined') {
                        window.localStorage.setItem(AUID_STORAGE_KEY, auid);
                    }
                } else {
                    clearAuid();
                }
                await loadProgress(activeToken);
            } catch (_error) {
                setStoredToken(null);
                setUser(null);
                setHighestLesson(null);
                setResumeLesson(null);
                clearAuid();
            } finally {
                setReady(true);
            }
        },
        [authConfigured, authRequest, clearAuid, identify, loadProgress, setStoredToken]
    );

    const login = useCallback(
        async ({ email, password, rememberMe = false }: ApiLoginData) => {
            if (!authConfigured) {
                throw new Error('Authentication is unavailable right now.');
            }

            setLoading(true);
            try {
                const data = await authRequest('auth/login', {
                    body: {
                        email,
                        password,
                        remember_me: rememberMe,
                    },
                    method: 'POST',
                    token: null,
                });
                const nextToken = getAccessToken(data);
                if (!nextToken) {
                    throw new Error(getErrorMessage(data, 'Unable to log in.'));
                }

                setStoredToken(nextToken);
                return data;
            } finally {
                setLoading(false);
            }
        },
        [authConfigured, authRequest, setStoredToken]
    );

    const join = useCallback(
        async ({ email, firstName, lastName, password, subscribeToNews = true }: ApiRegistrationData) => {
            if (!authConfigured) {
                throw new Error('Authentication is unavailable right now.');
            }

            setLoading(true);
            try {
                const data = await authRequest('auth/registration', {
                    body: {
                        email,
                        first_name: firstName,
                        last_name: lastName || null,
                        password,
                        remember_me: false,
                        subscribe_to_news: subscribeToNews,
                    },
                    method: 'POST',
                    token: null,
                });
                const nextToken = getAccessToken(data);
                if (!nextToken) {
                    throw new Error(getErrorMessage(data, 'Unable to create account.'));
                }

                setStoredToken(nextToken);
                if (subscribeToNews) {
                    void trackLeadEvent('lead_soft_newsletter_subscribed', {
                        auth_state: 'member',
                        resource_type: 'newsletter',
                        source: 'registration_opt_in',
                        consent_newsletter: true,
                    });
                }
                return data;
            } finally {
                setLoading(false);
            }
        },
        [authConfigured, authRequest, setStoredToken, trackLeadEvent]
    );

    const logout = useCallback(async () => {
        if (authConfigured && token) {
            try {
                await authRequest('auth/logout', {
                    method: 'POST',
                });
            } catch (_error) {
                // Proceed with local logout even if the server call fails
            }
        }

        setHighestLesson(null);
        setResumeLesson(null);
        setUser(null);
        setStoredToken(null);
        clearAuid();
        setReady(true);
    }, [authConfigured, authRequest, clearAuid, setStoredToken, token]);

    const saveProgress = useCallback(
        async (lesson: string, state?: Record<string, any>) => {
            const normalizedLesson = normalizeLesson(lesson);
            if (!authConfigured || !token || !normalizedLesson) {
                return;
            }

            const currentHighestIndex = highestLesson ? setup.lessonKeys.indexOf(highestLesson) : -1;
            const currentLessonIndex = setup.lessonKeys.indexOf(normalizedLesson);
            const nextHighestLesson = currentLessonIndex > currentHighestIndex ? normalizedLesson : highestLesson;

            const payload: Record<string, any> = {
                completed: normalizedLesson === setup.lessonKeys[setup.lessonKeys.length - 1],
                highest_checkpoint_type: 'lesson',
                highest_checkpoint_value: nextHighestLesson || normalizedLesson,
                last_checkpoint_type: 'lesson',
                last_checkpoint_value: normalizedLesson,
            };
            if (state) {
                payload.state = state;
            }

            try {
                const response = await authRequest(`kromatic/game-progress/${GAME_SLUG}`, {
                    body: payload,
                    method: 'PUT',
                });
                const progressLessons = parseProgressLessons(response);
                setHighestLesson(progressLessons.highestLesson || payload.highest_checkpoint_value);
                setResumeLesson(progressLessons.lastLesson || normalizedLesson);
            } catch (error) {
                console.error(error);
            }
        },
        [authConfigured, authRequest, highestLesson, token]
    );

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        setStoredToken(window.localStorage.getItem(TOKEN_STORAGE_KEY));
        setBootstrapped(true);
    }, [setStoredToken]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const handleStorage = (event: StorageEvent) => {
            if (event.key && event.key !== TOKEN_STORAGE_KEY) {
                return;
            }

            const nextToken = window.localStorage.getItem(TOKEN_STORAGE_KEY);
            setToken(nextToken);

            if (!nextToken) {
                setHighestLesson(null);
                setResumeLesson(null);
                setUser(null);
                clearAuid();
            }
        };

        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, [clearAuid]);

    useEffect(() => {
        if (!bootstrapped) {
            return;
        }

        if (!token) {
            setHighestLesson(null);
            setResumeLesson(null);
            setUser(null);
            setReady(true);
            return;
        }

        setReady(false);
        void refreshSession(token);
    }, [bootstrapped, token, refreshSession]);

    const state: ApiState = {
        authConfigured,
        loading,
        resumeLesson,
        token,
        user,
        join,
        login,
        logout,
        post: (path: string, data?: any) => {
            if (path === 'deal' || path === 'evaluate') {
                return workerPost(path, data);
            }
            throw new Error(`Unknown API path: ${path}`);
        },
        saveProgress,
    };

    return (
        <ApiContext.Provider value={state}>
            {ready ? children : <Box padding={3} textAlign="center"><CircularProgress color="inherit" /></Box>}
        </ApiContext.Provider>
    );
}
